import type {
  ProtectedAreaLayerKey,
  QuantitySummary,
  ZEDrawingFeatureCollection,
  ZEElementType,
} from "./types";

// ── protected areas (GDOŚ) — mirror of backend apps/geo/services/gdos.py GDOS_LAYERS.
// Colors follow the GDOŚ/QGIS convention (PN green, Rezerwaty orange, PK yellow,
// N2000 OSO blue, N2000 SOO red).
export const PROTECTED_AREA_LAYERS: Record<ProtectedAreaLayerKey, { label: string; color: string }> = {
  park_narodowy: { label: "Park Narodowy", color: "#1c7a1c" },
  rezerwat: { label: "Rezerwat", color: "#e97600" },
  park_krajobrazowy: { label: "Park Krajobrazowy", color: "#d4b800" },
  natura2000_oso: { label: "Natura 2000 OSO", color: "#2b6cb0" },
  natura2000_soo: { label: "Natura 2000 SOO", color: "#c53030" },
};

export function protectedAreaLabel(key: string): string {
  return PROTECTED_AREA_LAYERS[key as ProtectedAreaLayerKey]?.label ?? key;
}

export function protectedAreaColor(key: string): string {
  return PROTECTED_AREA_LAYERS[key as ProtectedAreaLayerKey]?.color ?? "#16a34a";
}

/** Pretty-print a length in metres (pl-PL): metres, or km above 1000 m. */
export function fmtMeters(m: number | null | undefined): string {
  if (m == null) return "—";
  if (m >= 1000) return `${(m / 1000).toLocaleString("pl-PL", { maximumFractionDigits: 2 })} km`;
  return `${m.toLocaleString("pl-PL", { maximumFractionDigits: 0 })} m`;
}

export function fmtArea(m2: number | null | undefined): string {
  if (m2 == null) return "—";
  return `${m2.toLocaleString("pl-PL", { maximumFractionDigits: 0 })} m²`;
}

// Default palette for element types whose `color` is blank — deterministic by order.
const PALETTE = ["#dc2626", "#2563eb", "#7c3aed", "#0d9488", "#ea580c", "#65a30d", "#0891b2", "#db2777"];

export function colorForType(t: ZEElementType | undefined, index = 0): string {
  if (t?.color) return t.color;
  return PALETTE[(t?.order ?? index) % PALETTE.length] ?? "#dc1f1f";
}

const RING_STROKE = "#ea580c"; // amber accent for modernizowana/przebudowywana

/** Leaflet path style for a line element type (colour + dashed=napowietrzna). */
export function lineStyleFor(t: ZEElementType | undefined): { color: string; weight: number; dashArray?: string } {
  return { color: colorForType(t), weight: 3, dashArray: t?.dash || undefined };
}

/** Leaflet path style for an area element type. Deliberately translucent: powierzchnie
 *  do odtworzenia leżą NA trasie, więc muszą czytelnie przepuszczać to, co pod nimi —
 *  inaczej obrys asfaltu zasłania kabel, którego dotyczy. */
export function areaStyleFor(
  t: ZEElementType | undefined,
): { color: string; weight: number; fillColor: string; fillOpacity: number; dashArray?: string } {
  const color = colorForType(t);
  return {
    color,
    weight: 2,
    fillColor: color,
    fillOpacity: 0.25,
    dashArray: t?.dash || undefined,
  };
}

/** SVG swatch for an area type — a filled rectangle, matching the map style. */
export function areaSvg(t: ZEElementType | undefined, w = 24, h = 14): string {
  const color = colorForType(t);
  return `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg"><rect x="1.5" y="1.5" width="${w - 3}" height="${h - 3}" fill="${color}" fill-opacity="0.25" stroke="${color}" stroke-width="2" /></svg>`;
}

/** SVG markup for an object marker: shape = kind, colour = voltage/subtype,
 *  fill = status (solid=projektowana / hollow=likwidowana / ring=modernizowana /
 *  dot=istniejące nawiązanie / half=wymiana — shape half-filled "stary → nowy").
 *  Shared by the map (divIcon) and the palette. */
export function objectSvg(t: ZEElementType | undefined, size = 18): string {
  const s = size;
  const c = s / 2;
  const shape = (t?.shape as ZEElementType["shape"]) || "circle";
  const fill = (t?.fill as ZEElementType["fill"]) || "solid";
  const color = colorForType(t);
  const stroke = fill === "ring" ? RING_STROKE : color;
  const sw = fill === "solid" ? 1 : fill === "ring" ? 2.5 : 2;
  const fillColor = fill === "hollow" || fill === "dot" || fill === "half" ? "#ffffff" : color;
  let el: string;
  // visual centre of the shape (triangle centroid sits below the box centre)
  let dotY = c;
  switch (shape) {
    case "square":
      el = `<rect x="2" y="2" width="${s - 4}" height="${s - 4}" rx="1" />`;
      break;
    case "diamond":
      el = `<polygon points="${c},1.5 ${s - 1.5},${c} ${c},${s - 1.5} 1.5,${c}" />`;
      break;
    case "triangle":
      el = `<polygon points="${c},2 ${s - 2},${s - 2} 2,${s - 2}" />`;
      dotY = (2 + 2 * (s - 2)) / 3;
      break;
    default:
      el = `<circle cx="${c}" cy="${c}" r="${c - 2}" />`;
  }
  // "half" (wymiana): white shape with its LEFT half painted in colour (clipped to the
  // shape), then a crisp full outline on top. id keyed by shape+colour+size so identical
  // markers share one clip and different ones never collide across inline SVGs on the page.
  if (fill === "half") {
    const clipId = `ze-half-${shape}-${color.replace("#", "")}-${s}`;
    return `<svg width="${s}" height="${s}" viewBox="0 0 ${s} ${s}" xmlns="http://www.w3.org/2000/svg">` +
      `<defs><clipPath id="${clipId}">${el}</clipPath></defs>` +
      `<g fill="#ffffff" stroke="none">${el}</g>` +
      `<rect x="0" y="0" width="${c}" height="${s}" fill="${color}" stroke="none" clip-path="url(#${clipId})" />` +
      `<g fill="none" stroke="${color}" stroke-width="${sw}" stroke-linejoin="round">${el}</g>` +
      `</svg>`;
  }
  const dot = fill === "dot" ? `<circle cx="${c}" cy="${dotY}" r="${s / 7}" fill="${color}" stroke="none" />` : "";
  return `<svg width="${s}" height="${s}" viewBox="0 0 ${s} ${s}" xmlns="http://www.w3.org/2000/svg"><g fill="${fillColor}" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round">${el}${dot}</g></svg>`;
}

/** SVG markup for a line swatch (palette legend). */
export function lineSvg(t: ZEElementType | undefined, w = 24, h = 12): string {
  const color = colorForType(t);
  const dash = t?.dash ? ` stroke-dasharray="${t.dash}"` : "";
  return `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg"><line x1="1" y1="${h / 2}" x2="${w - 1}" y2="${h / 2}" stroke="${color}" stroke-width="3"${dash} stroke-linecap="round" /></svg>`;
}

/** Unified swatch (line, area or object) for palette / legend, as an SVG string. */
export function elementSwatchSvg(t: ZEElementType | undefined): string {
  if (t?.kind === "line") return lineSvg(t);
  if (t?.kind === "area") return areaSvg(t);
  return objectSvg(t, 16);
}

// Colours for ownership codes (kod własności), grouped by EGiB register group so
// parcels with the same owner kind share one colour.
// Grupy „w zbiegu z użytkownikiem wieczystym" (2/5/12/14) dziedziczą kolor właściciela
// gruntu (1/4/11/13) — to nadal działka urzędowa tej samej instytucji, tylko z drugim
// adresatem (użytkownikiem wieczystym).
const OWNERSHIP_GROUP_COLORS: Record<string, string> = {
  "1":  "#7c3aed", // Starostwo — fioletowy
  "2":  "#7c3aed", // Skarb Państwa w zbiegu z użytkownikiem wieczystym — jak 1
  "4":  "#2563eb", // Gmina — niebieski
  "5":  "#2563eb", // Gmina w zbiegu z użytkownikiem wieczystym — jak 4
  "11": "#0284c7", // ZPD — jasnoniebieski
  "12": "#0284c7", // Powiat w zbiegu z użytkownikiem wieczystym — jak 11
  "13": "#6366f1", // ZDW — indigo
  "14": "#6366f1", // Województwo w zbiegu z użytkownikiem wieczystym — jak 13
  "7":  "#f87171", // WPW Prywatny — jasnoczerwony
  "15": "#f87171", // WPW Prywatny — jasnoczerwony
};
const OWNERSHIP_COLOR_NONE = "#9ca3af"; // brak kodu
const OWNERSHIP_COLOR_OTHER = "#9333ea"; // pozostałe kody

/** Colour for an ownership code (kod własności), grouped by EGiB register group. */
export function ownershipCodeColor(code: string): string {
  const c = code?.trim();
  if (!c) return OWNERSHIP_COLOR_NONE;
  // Numeric ULDK codes resolve via their register group; manual override codes
  // (e.g. wpw_droga_wewnetrzna, wpw_dzialka_koncowa) carry their own colour on the
  // option so the parcel fill matches the legend/popup instead of falling to OTHER.
  return OWNERSHIP_GROUP_COLORS[c] ?? findOwnershipOption(c)?.color ?? OWNERSHIP_COLOR_OTHER;
}

// ── ownership options (ULDK codes + manual labels) ───────────────────────────

export interface OwnershipOption {
  value: string;
  label: string;
  group: "uldk" | "manual";
  isWpw: boolean;
  wpwDefault: number | null;
  /** How to determine unique-office count in summary */
  uniqueLevel: "gmina" | "powiat" | "woj" | "parcel" | "wpw";
  color: string;
}

export const OWNERSHIP_OPTIONS: OwnershipOption[] = [
  // ── Kody ULDK (domyślne, z grupy rejestrowej EGiB) ──
  { value: "4",  label: "4 — Gmina",               group: "uldk",   isWpw: false, wpwDefault: null, uniqueLevel: "gmina",  color: "#2563eb" }, // niebieski
  // Grupy „w zbiegu z użytkownikiem wieczystym" (5/12/14/2): działka urzędowa właściciela
  // gruntu (liczona do tych samych unikalnych urzędów co 4/11/13/1) + drugi adresat.
  { value: "5",  label: "5 — Gmina (użytk. wieczyste)", group: "uldk", isWpw: false, wpwDefault: null, uniqueLevel: "gmina",  color: "#2563eb" }, // niebieski
  { value: "11", label: "11 — ZPD",                group: "uldk",   isWpw: false, wpwDefault: null, uniqueLevel: "powiat", color: "#0284c7" }, // jasnoniebieski
  { value: "12", label: "12 — ZPD (użytk. wieczyste)", group: "uldk", isWpw: false, wpwDefault: null, uniqueLevel: "powiat", color: "#0284c7" }, // jasnoniebieski
  { value: "13", label: "13 — ZDW",                group: "uldk",   isWpw: false, wpwDefault: null, uniqueLevel: "woj",   color: "#6366f1" }, // indigo
  { value: "14", label: "14 — ZDW (użytk. wieczyste)", group: "uldk", isWpw: false, wpwDefault: null, uniqueLevel: "woj",   color: "#6366f1" }, // indigo
  { value: "7",  label: "7 — WPW Prywatny",        group: "uldk",   isWpw: true,  wpwDefault: 1.5,  uniqueLevel: "wpw",   color: "#f87171" }, // jasnoczerwony
  { value: "15", label: "15 — WPW Prywatny",       group: "uldk",   isWpw: true,  wpwDefault: 1.5,  uniqueLevel: "wpw",   color: "#f87171" }, // jasnoczerwony
  { value: "1",  label: "1 — Starostwo Powiatowe", group: "uldk",   isWpw: false, wpwDefault: null, uniqueLevel: "powiat", color: "#7c3aed" }, // fioletowy
  { value: "2",  label: "2 — Skarb Państwa (użytk. wieczyste)", group: "uldk", isWpw: false, wpwDefault: null, uniqueLevel: "powiat", color: "#7c3aed" }, // fioletowy
  // ── Ręczne ──
  { value: "lasy_panstwowe",       label: "Lasy Państwowe",             group: "manual", isWpw: false, wpwDefault: null, uniqueLevel: "parcel", color: "#16a34a" }, // zielony
  { value: "wody_polskie",         label: "Wody Polskie",               group: "manual", isWpw: false, wpwDefault: null, uniqueLevel: "parcel", color: "#1d4ed8" }, // niebieski (WP)
  { value: "gddkia",               label: "GDDKiA",                     group: "manual", isWpw: false, wpwDefault: null, uniqueLevel: "parcel", color: "#d97706" }, // bursztynowy
  { value: "pkp",                  label: "PKP",                        group: "manual", isWpw: false, wpwDefault: null, uniqueLevel: "parcel", color: "#111827" }, // czarny
  { value: "wpw_droga_wewnetrzna", label: "WPW Prywatny — droga wewn.", group: "manual", isWpw: true,  wpwDefault: 7,    uniqueLevel: "wpw",   color: "#e11d48" }, // czerwony
  // Kod 7 (WPW prywatny) — wariant dla działki końcowej trasy; backend liczy WPW po surowym
  // ownership_code ∈ {7,15} + wpw_count, więc nadpisanie tylko etykietuje/koloruje osobno.
  { value: "wpw_dzialka_koncowa",  label: "WPW Prywatny (działka końcowa)", group: "manual", isWpw: true,  wpwDefault: 1.5,  uniqueLevel: "wpw",   color: "#7e22ce" }, // fioletowy
];

/** Manual override marking a private parcel as an internal road (mirrors the backend's
 *  INTERNAL_ROAD_KIND) — drives the road-neighbour discovery and the
 *  ilosc_dz_prv_osc_dr_wewn* pricing keys. */
export const INTERNAL_ROAD_KIND = "wpw_droga_wewnetrzna";

export function findOwnershipOption(value: string): OwnershipOption | undefined {
  return OWNERSHIP_OPTIONS.find((o) => o.value === value);
}

// ── local (client-side) quantity estimate from the drawn GeoJSON ─────────────
// Approximate: lengths are great-circle (EPSG:4326) — the backend value (EPSG:2180) is
// authoritative after save. Used only for instant feedback while drawing.
const R = 6371008.8; // mean Earth radius (m)
function haversine(a: [number, number], b: [number, number]): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(a[0] - b[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function lineLength(coords: unknown): number {
  if (!Array.isArray(coords)) return 0;
  // LineString = [[lng,lat],...]; MultiLineString = [[[lng,lat],...],...]
  if (Array.isArray(coords[0]) && Array.isArray((coords[0] as unknown[])[0])) {
    return (coords as unknown[]).reduce<number>((acc, part) => acc + lineLength(part), 0);
  }
  let total = 0;
  const pts = coords as [number, number][];
  for (let i = 1; i < pts.length; i++) total += haversine(pts[i - 1], pts[i]);
  return total;
}

/** Planar ring area via the shoelace formula, in m².
 *
 *  Degrees are scaled to metres locally (longitude shrinks by cos(lat)) before the sum,
 *  which is accurate enough at the scale a single restoration patch is drawn — this is a
 *  live preview only. The authoritative number comes from PostGIS in EPSG:2180 the
 *  moment the drawing is saved. */
function ringArea(ring: [number, number][]): number {
  if (ring.length < 3) return 0;
  const latRad = (ring[0][1] * Math.PI) / 180;
  const mPerDegLat = 111132.92;
  const mPerDegLng = 111412.84 * Math.cos(latRad);
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    sum += x1 * mPerDegLng * (y2 * mPerDegLat) - x2 * mPerDegLng * (y1 * mPerDegLat);
  }
  return Math.abs(sum) / 2;
}

/** Polygon / MultiPolygon area in m². Interior rings (holes) are subtracted. */
function polygonArea(coords: unknown): number {
  if (!Array.isArray(coords) || !coords.length) return 0;
  const first = coords[0] as unknown;
  // MultiPolygon = [[[ [lng,lat], … ], …], …] — one nesting level deeper than Polygon.
  if (Array.isArray(first) && Array.isArray((first as unknown[])[0]) &&
      Array.isArray(((first as unknown[])[0] as unknown[])[0])) {
    return (coords as unknown[]).reduce<number>((acc, part) => acc + polygonArea(part), 0);
  }
  const rings = coords as [number, number][][];
  return rings.reduce((acc, ring, i) => acc + (i === 0 ? ringArea(ring) : -ringArea(ring)), 0);
}

export function localQuantitySummary(
  fc: ZEDrawingFeatureCollection,
  typesByCode: Record<string, ZEElementType>,
): QuantitySummary {
  const lineAcc: Record<string, number> = {};
  const objAcc: Record<string, number> = {};
  const areaAcc: Record<string, { area: number; perimeter: number }> = {};
  for (const f of fc.features ?? []) {
    const code = f.properties?.element_type;
    const t = code ? typesByCode[code] : undefined;
    if (!t) continue;
    if (t.kind === "line") {
      lineAcc[code] = (lineAcc[code] ?? 0) + lineLength(f.geometry?.coordinates);
    } else if (t.kind === "area") {
      const acc = (areaAcc[code] ??= { area: 0, perimeter: 0 });
      acc.area += polygonArea(f.geometry?.coordinates);
      // A polygon's rings are closed line strings, so the same walk gives the perimeter.
      acc.perimeter += lineLength(f.geometry?.coordinates);
    } else {
      objAcc[code] = (objAcc[code] ?? 0) + 1;
    }
  }
  const params: Record<string, number> = {};
  const lines = Object.entries(lineAcc).map(([code, m]) => {
    const v = Math.round(m * 100) / 100;
    params[code] = v;
    const t = typesByCode[code];
    return { element_type: code, label: t?.label ?? code, unit: t?.unit ?? "m", total_length_m: v };
  });
  const objects = Object.entries(objAcc).map(([code, count]) => {
    params[code] = count;
    const t = typesByCode[code];
    return { element_type: code, label: t?.label ?? code, unit: t?.unit ?? "szt", count };
  });
  const areas = Object.entries(areaAcc).map(([code, acc]) => {
    const v = Math.round(acc.area * 100) / 100;
    params[code] = v;
    const t = typesByCode[code];
    return {
      element_type: code,
      label: t?.label ?? code,
      unit: t?.unit ?? "m2",
      total_area_m2: v,
      total_perimeter_m: Math.round(acc.perimeter * 100) / 100,
    };
  });
  return { lines, objects, areas, params };
}
